import store from '@/store';
import logger from '@/modules/Logger';

import { DiscoveryMaterialService } from '@/modules/apiSelector/DiscoveryMaterialService';
import { GetVideoDataService } from '@/modules/apiSelector/GetVideoDataService';
import { GetPdfService } from '@/modules/apiSelector/GetPdfService';
import { GetMaterialService } from './GetMaterialService';
import { StoreVocabularyService } from '@/modules/apiSelector/StoreVocabularyService';
import { CategorizedWordService } from '@/modules/apiSelector/CategorizedWordService';
import { GetTranslationService } from '@/modules/apiSelector/GetTranslationService';
import { UpdateVocabularyNoteService } from './UpdateVocabularyNoteService';
import { GetUsefulPhrasesService } from '@/modules/apiSelector/GetUsefulPhrasesService';
import { UpdateConfirmCheckListPopupService } from '@/modules/apiSelector/UpdateConfirmCheckListPopupService';
import { GetHtmlService } from '@/modules/apiSelector/GetHtmlService';
import { GetUserTypeHtmlService } from '@/modules/apiSelector/GetUserTypeHtmlService';
import { GetLessonTokenService } from '@/modules/apiSelector/GetLessonTokenService';
import { GetAdminTrainerLessonTokenService } from '@/modules/apiSelector/GetAdminTrainerLessonTokenService';
import { RecordingService } from '@/modules/apiSelector/RecordingService';
import { SetImportantDataService } from '@/modules/apiSelector/SetImportantDataService';
import { CustomerFeedbackSettingService } from '@/modules/apiSelector/CustomerFeedbackSettingService';
import { LogCustomerFeedbackViewService } from '@/modules/apiSelector/LogCustomerFeedbackViewService';
import { CustomerFeedbackQuestionsService } from '@/modules/apiSelector/CustomerFeedbackQuestionsService';
import { UpdateDislikeSettingService } from '@/modules/apiSelector/UpdateDislikeSettingService';
import { GetTagOptionListService } from '@/modules/apiSelector/GetTagOptionListService';
import { GetTaggedWordHistoryService } from '@/modules/apiSelector/GetTaggedWordHistoryService';
import { StoreTrainerTagService } from '@/modules/apiSelector/StoreTrainerTagService';
import { DeleteTrainerTagService } from '@/modules/apiSelector/DeleteTrainerTagService';
import { SaveCustomerFeedbackService } from '@/modules/apiSelector/SaveCustomerFeedbackService';
import { IncrementLessonHabitService } from '@/modules/apiSelector/IncrementLessonHabitService';
import { LogMyStageEventService } from '@/modules/apiSelector/LogMyStageEventService';
import { IfUseNewChatHistoryState } from '@/modules/apiSelector/state/IfUseNewChatHistoryState';
import { GetChatHistoryService } from '@/modules/apiSelector/GetChatHistoryService';
import { UpdateChatMessageService } from '@/modules/apiSelector/UpdateChatMessageService';
import { DeleteChatMessageService } from '@/modules/apiSelector/DeleteChatMessageService';
import { GetUserOtherSettingService } from '@/modules/apiSelector/GetUserOtherSettingService';
import { GetMaterialTokenService } from '@/modules/apiSelector/GetMaterialTokenService';
import { GetAdminDevTestTokenService } from '@/modules/apiSelector/GetAdminDevTestTokenService';
import { GetAgoraTokenService } from '@/modules/apiSelector/GetAgoraTokenService';
import { isZipanHost } from '@/utils/urlHelper';
import { enableCanaryCheck } from '@/utils/constants';

/**
 * API Service Registry
 *
 * Each service can define different implementations based on user type:
 * - `msb`   : Function to execute for canary users
 * - `mbti`  : Function to execute for non-canary `s` users
 * - `legacy`: Function to execute for non-canary `t` and `a` users
 * - `beta`: Indicates if the API is in beta
 * - `betaAccessLevel`: Minimum value of `student_other_setting.enable_beta` 
 *                      required for access
 */
const API_SERVICES = {
  getLessonToken: { ...GetLessonTokenService, beta: false, betaAccessLevel: 1 },
  getAdminTrainerLessonToken: {...GetAdminTrainerLessonTokenService, beta: false, betaAccessLevel: 1},

  discoveryMaterial: { ...DiscoveryMaterialService, beta: false, betaAccessLevel: 2 },
  getVideoData:  { ...GetVideoDataService, beta: false, betaAccessLevel: 2 },
  getPdfData: { ...GetPdfService, beta: false, betaAccessLevel: 2 },
  getMaterial: { ...GetMaterialService, beta: false, betaAccessLevel: 2 },
  getHtmlData: { ...GetHtmlService, beta: false, betaAccessLevel: 2 },
  getMaterialToken: { ...GetMaterialTokenService, beta: false, betaAccessLevel: 2 },
  getUserTypeHtmlData: { ...GetUserTypeHtmlService, beta: false, betaAccessLevel: 2 },

  getCategorizedWords: { ...CategorizedWordService, beta: false, betaAccessLevel: 3 },
  getTranslation: { ...GetTranslationService, beta: false, betaAccessLevel: 3 },
  updateVocabularyNote: { ...UpdateVocabularyNoteService, beta: false, betaAccessLevel: 3 },
  storeVocabulary: { ...StoreVocabularyService, beta: false, betaAccessLevel: 3 },
  getUsefulPhrases: { ...GetUsefulPhrasesService, beta: false, betaAccessLevel: 3 },
  updateConfirmCheckListPopup: { ...UpdateConfirmCheckListPopupService, beta: false, betaAccessLevel: 3 },
  executeRecording: { ...RecordingService, beta: false, betaAccessLevel: 3 },
  setImportantData: { ...SetImportantDataService, beta: false, betaAccessLevel: 3 },
  customerFeedbackSetting: { ...CustomerFeedbackSettingService, beta: false, betaAccessLevel: 3 },
  logCustomerFeedbackView: { ...LogCustomerFeedbackViewService, beta: false, betaAccessLevel: 3 },
  customerFeedbackQuestions: { ...CustomerFeedbackQuestionsService, beta: false, betaAccessLevel: 3 },
  saveCustomerFeedback: { ...SaveCustomerFeedbackService, beta: false, betaAccessLevel: 3 },
  updateDislikeSetting: { ...UpdateDislikeSettingService, beta: false, betaAccessLevel: 3 },
  logMyStage: { ...LogMyStageEventService, beta: false, betaAccessLevel: 3 },
  getUserOtherSetting: { ...GetUserOtherSettingService, beta: false, betaAccessLevel: 3 },

  getTagOptionList: { ...GetTagOptionListService, beta: false, betaAccessLevel: 4 },
  getTaggedWordHistory: { ...GetTaggedWordHistoryService, beta: false, betaAccessLevel: 4 },
  storeTrainerTag: { ...StoreTrainerTagService, beta: false, betaAccessLevel: 4 },
  deleteTrainerTag: { ...DeleteTrainerTagService, beta: false, betaAccessLevel: 4 },
  incrementLessonHabit: { ...IncrementLessonHabitService, beta: false, betaAccessLevel: 4 },
  getChatHistory: { ...GetChatHistoryService, beta: false, betaAccessLevel: 4 },
  updateChatMessage: { ...UpdateChatMessageService, beta: false, betaAccessLevel: 4 },
  deleteChatMessage: { ...DeleteChatMessageService, beta: false, betaAccessLevel: 4 },
  ifUseNewChatHistory: { ...IfUseNewChatHistoryState, beta: false, betaAccessLevel: 4 },

  getAdminDevTestToken: { ...GetAdminDevTestTokenService, beta: false, betaAccessLevel: 4 },
  agoraToken: { ...GetAgoraTokenService, beta: false, betaAccessLevel: 4 },
};

// set the default API key base on system required fallback
// when users canary setting cannot be detected
// e.g. when enableCanaryCheck constant is set to `false`
const DEFAULT_API_KEY = 'msb';
const ACCOUNT_TYPE_API_KEY = {
  s: { old: 'mbti'  ,  new: 'msb'},
  t: { old: 'legacy',  new: 'msb'},
  a: { old: 'legacy',  new: 'msb'},
}

/**
 * Determines the appropriate API endpoint (legacy or new) based on the user's canary status
 * @param {string} endpoint - API endpoint (e.g., "discoveryMaterial").
 * @returns {Function} - Corresponding API method (msb, mbti, legacy)
 */
export function getApiMethod(endpoint) {
  let endpointDetail = API_SERVICES[endpoint];
  if (!endpointDetail) {
    return;
  }

  const hasBetaAccess = store.state.enableBeta >= endpointDetail.betaAccessLevel;
  const isCanaryUser = hasBetaAccess && !store.state.isTrial;
  const isZipanService = isZipanHost;
  const isNewApi = !enableCanaryCheck 
    || isCanaryUser 
    || isZipanService 
    || !endpointDetail.beta 
    || store.state.actualAcctType === 'a';
  const accountApiKeys = ACCOUNT_TYPE_API_KEY[store.state.actualAcctType]; 

  let apiKey;
  // force legacy key if isLegacy is true
  if (store.getters['isLegacy']) {
    apiKey = 'legacy';
  } else {
    // Prefer new API key if it's intended and valid
    const newKey = accountApiKeys?.new;
    const oldKey = accountApiKeys?.old;
    if (isNewApi && newKey && endpointDetail[newKey]) {
      apiKey = newKey;
    } else if (oldKey && endpointDetail[oldKey]) {
      apiKey = oldKey;
    } else {
      apiKey = isZipanService ? 'msb' : DEFAULT_API_KEY;
    }
  }

  let logMessage = `Using ${apiKey} endpoint`;
  logger.debug(`[Canary Release][${endpoint}] ${logMessage}`, {
    operation: 'lesson:detect-canary-user', 
    enableBeta: store.state.enableBeta, 
    accountType: store.state.actualAcctType, 
    isNewApi: isNewApi, 
    isLegacy: store.getters['isLegacy'], 
    beta: endpointDetail.beta,
    betaAccessLevel: endpointDetail.betaAccessLevel
  }); 
  
  return endpointDetail[apiKey];
}
